import {
  getDropdownDivider,
  getEditMetamodelMenuItem,
  getEditWorkspaceMenuItem,
  getExcludeWorkspaceMenuItem,
  getFilterWorkspaceMenuItem,
  getPermissionsMenuItem,
} from './menuItems';
import { isTruthy } from '@ardoq/common-helpers';
import { activeScenarioOperations } from '@ardoq/core';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import { TrackedContextMenus, getTrackingFunction } from './tracking';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { GetContextMenuOptionsArguments } from '@ardoq/context-menu';
import { APIWorkspaceAttributes, ArdoqId } from '@ardoq/api-types';
import { type ScenarioModeState } from '@ardoq/data-model';
import { toIds } from '@ardoq/core';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { Features } from '@ardoq/features';
import { PermissionContext } from '@ardoq/access-control';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { type I18nLocale } from '@ardoq/i18n';

/** What the workspace menu needs from the app, beyond the workspaces it is for. */
export type WorkspaceMenuState = {
  scenarioId: ArdoqId | null;
  isDiffMode: boolean;
  /** The workspaces in context: excluding the last of them from a scenario is not offered. */
  loadedWorkspaceIds: ArdoqId[];
};

export const toWorkspaceMenuState = (
  activeScenarioState: ScenarioModeState,
  loadedWorkspaceIds: ArdoqId[]
): WorkspaceMenuState => ({
  scenarioId: activeScenarioOperations.getActiveScenarioId(activeScenarioState),
  isDiffMode: activeScenarioOperations.isInDiffMode(activeScenarioState),
  loadedWorkspaceIds,
});

type GetWorkspaceMenuArgs = {
  workspaces: APIWorkspaceAttributes[];
  isViewpointMode: boolean;
  permissionContext: PermissionContext;
  i18nLocale: I18nLocale;
} & WorkspaceMenuState &
  GetContextMenuOptionsArguments;
export const getWorkspaceMenu = ({
  workspaces,
  scenarioId,
  isDiffMode,
  loadedWorkspaceIds,
  target,
  isViewpointMode,
  permissionContext,
  i18nLocale,
}: GetWorkspaceMenuArgs): DropdownItem[] | null => {
  const workspaceIds = toIds(workspaces);

  const menuItemWorkspaceTrackingFunction = getTrackingFunction(
    target,
    TrackedContextMenus.WORKSPACE_CONTEXT_MENU
  );

  const hasAdminAccess = workspaceAccessControlInterface.canAdminWorkspace(
    permissionContext,
    workspaceIds[0],
    // we ignore the scenario state here, because we want to show the edit options anyways
    // but we disabled them if it is a scenario mode.
    null
  );

  const hasQuickStartFeature = hasFeature(Features.QUICK_START);
  // KFQ12026
  const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);

  const editWorkspaceMenuItem = getEditWorkspaceMenuItem(
    workspaceIds[0],
    menuItemWorkspaceTrackingFunction,
    isCanvasPrototype,
    i18nLocale
  );

  const permissionsMenuItem = getPermissionsMenuItem(
    workspaceIds[0],
    workspaces[0].name,
    menuItemWorkspaceTrackingFunction,
    i18nLocale
  );

  const menu = [
    hasAdminAccess &&
      !hasQuickStartFeature &&
      !isCanvasPrototype &&
      getEditMetamodelMenuItem(
        workspaceIds[0],
        menuItemWorkspaceTrackingFunction,
        i18nLocale
      ),
    hasAdminAccess && editWorkspaceMenuItem,
    hasAdminAccess && getDropdownDivider(),
    hasAdminAccess && permissionsMenuItem,
    hasAdminAccess && getDropdownDivider(),
    !isViewpointMode &&
      getFilterWorkspaceMenuItem(
        workspaceIds[0],
        workspaces[0].name,
        menuItemWorkspaceTrackingFunction,
        i18nLocale
      ),
    !!scenarioId &&
      !isDiffMode &&
      scenarioAccessControlInterface.canAdminScenario(
        permissionContext,
        scenarioId
      ) &&
      getExcludeWorkspaceMenuItem(
        scenarioId,
        workspaceIds[0],
        loadedWorkspaceIds.length <= 1,
        menuItemWorkspaceTrackingFunction,
        i18nLocale
      ),
  ].filter(isTruthy);

  return menu.length ? menu : null;
};
